import React, { useEffect, useMemo, useState } from "react";
import { Tabs } from "@amzn/awsui-components-react";

export interface VersionSelectorProps<T> {
  data: T[];
  getVersion: (item: T) => number;
  renderTabLabel: (version: number, items: T[]) => React.ReactNode;
  renderTabContent: (items: T[]) => React.ReactNode;
  onVersionChange: (index: number) => void;
}

export default function VersionSelector<T>(props: VersionSelectorProps<T>) {
  const {
    data,
    getVersion,
    renderTabLabel,
    renderTabContent,
    onVersionChange,
  } = props;

  const didInitializeRef = React.useRef(false);
  const { sortedVersions, versionToIndexMap, versionTabs } = useMemo(() => {
    const uniqueVersions = Array.from(new Set(data.map(getVersion))).sort(
      (a, b) => b - a
    );

    const indexMap = new Map<number, number>();
    data.forEach((item, index) => {
      const version = getVersion(item);
      if (!indexMap.has(version)) {
        indexMap.set(version, index);
      }
    });

    const tabs = uniqueVersions.map((version) => {
      const itemsForVersion = data.filter(
        (item) => getVersion(item) === version
      );

      return {
        id: `version-${version}`,
        label: renderTabLabel(version, itemsForVersion),
        content: renderTabContent(itemsForVersion),
      };
    });

    return {
      sortedVersions: uniqueVersions,
      versionToIndexMap: indexMap,
      versionTabs: tabs,
    };
  }, [data, getVersion, renderTabLabel, renderTabContent]);

  const dataRef = React.useRef(data);

  const [selectedTabId, setSelectedTabId] = useState<string | undefined>(
    sortedVersions.length > 0 ? `version-${sortedVersions[0]}` : undefined
  );

  // When data changes significantly, reset the tab selection
  useEffect(() => {
    if (sortedVersions.length > 0) {
      const highestVersion = sortedVersions[0];
      const initialTabId = `version-${highestVersion}`;

      // Reset selection when data reference changes (flow switch) or sortedVersions changes
      const dataChanged = dataRef.current !== data;
      if (dataChanged) {
        setSelectedTabId(initialTabId);
        dataRef.current = data;
      }

      // Always notify parent of the selected index on mount
      if (!didInitializeRef.current) {
        const initialIndex = versionToIndexMap.get(highestVersion) || 0;
        onVersionChange(initialIndex);
        didInitializeRef.current = true;
      }
    }
  }, [data, sortedVersions, versionToIndexMap, onVersionChange]);

  const handleTabChange = (detail: { activeTabId: string }) => {
    const newTabId = detail.activeTabId;
    setSelectedTabId(newTabId);

    const selectedVersion = parseInt(newTabId.split("-")[1], 10);
    const selectedIndex = versionToIndexMap.get(selectedVersion) || 0;
    onVersionChange(selectedIndex);
  };

  return (
    <Tabs
      activeTabId={selectedTabId}
      tabs={versionTabs}
      onChange={({ detail }) => handleTabChange(detail)}
    />
  );
}
